import Keycloak from "keycloak-js";
import { fallbackAuthConfig } from "./config";
import type { Role, UserInfo } from "./types";

/**
 * Keycloak OIDC authentication client.
 *
 * Uses the keycloak-js library for authorization code flow with PKCE.
 * Tokens are stored in memory only (never localStorage) for security.
 */
export class KeycloakAuth {
  private keycloakInstance: Keycloak | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  /**
   * The in-flight (or settled) init call. The authorization code is single
   * use, so a second init races the first for it: one exchange wins, the
   * other fails, and `onLoad: "login-required"` sends the browser back to
   * Keycloak, which loops forever with an empty page. React StrictMode
   * invokes the effect twice in development and reproduced exactly that.
   * Every caller therefore shares one init.
   */
  private initPromise: Promise<boolean> | null = null;

  /** Keycloak realm URL. */
  private readonly url: string;
  /** OIDC client ID (public client, no secret). */
  private readonly clientId: string;

  constructor(
    // AuthContext resolves the issuer through resolveAuthConfig (the API
    // server first, D171) and passes it in. The defaults here are the
    // network-free part of that order, for callers that construct the
    // client directly.
    url: string = fallbackAuthConfig().keycloakUrl,
    clientId: string = fallbackAuthConfig().clientId,
  ) {
    this.url = url;
    this.clientId = clientId;
  }

  /**
   * Initialize Keycloak and attempt silent login via check-sso.
   * Returns true if the user is authenticated after init.
   */
  async init(): Promise<boolean> {
    this.initPromise ??= this.doInit();
    return this.initPromise;
  }

  private async doInit(): Promise<boolean> {
    // Dynamic import to avoid bundling keycloak-js when not needed in tests.
    const Keycloak = (await import("keycloak-js")).default;

    this.keycloakInstance = new Keycloak({
      url: this.url.replace(/\/realms\/.*$/, ""),
      realm: this.extractRealm(),
      clientId: this.clientId,
    });

    const authenticated = await this.keycloakInstance.init({
      onLoad: "login-required",
      pkceMethod: "S256",
      checkLoginIframe: false,
    });

    if (authenticated) {
      this.scheduleRefresh();
    }

    return authenticated;
  }

  /** Redirect to Keycloak login page. */
  login(): void {
    this.keycloakInstance?.login();
  }

  /** Redirect to Keycloak logout. */
  logout(): void {
    this.clearRefreshTimer();
    this.keycloakInstance?.logout({
      redirectUri: window.location.origin,
    });
  }

  /** Get the current access token, or null if not authenticated. */
  getToken(): string | null {
    return this.keycloakInstance?.token ?? null;
  }

  /**
   * Refresh the access token.
   * Returns true if refresh succeeded, false otherwise.
   */
  async refreshToken(): Promise<boolean> {
    if (!this.keycloakInstance) return false;

    try {
      // Refresh if token expires within 5 minutes (300 seconds).
      const refreshed = await this.keycloakInstance.updateToken(300);
      if (refreshed) {
        this.scheduleRefresh();
      }
      return true;
    } catch {
      // Refresh failed, user needs to re-authenticate.
      this.clearRefreshTimer();
      return false;
    }
  }

  /** Extract user information from the current token. */
  getUserInfo(): UserInfo | null {
    const kc = this.keycloakInstance;
    if (!kc?.tokenParsed) return null;

    const parsed = kc.tokenParsed as Record<string, unknown>;
    const realmAccess = parsed.realm_access as
      | { roles?: string[] }
      | undefined;
    const realmRoles = realmAccess?.roles ?? [];

    return {
      sub: (parsed.sub as string) ?? "",
      username: (parsed.preferred_username as string) ?? "",
      email: (parsed.email as string) ?? "",
      tenantId: (parsed.tenant_id as string) ?? "",
      role: mapRole(realmRoles),
      realmRoles,
    };
  }

  /** Whether the user is currently authenticated. */
  get authenticated(): boolean {
    return this.keycloakInstance?.authenticated ?? false;
  }

  // --- Private helpers ---

  /** Extract realm name from the URL (e.g., ".../realms/opendesk" -> "opendesk"). */
  private extractRealm(): string {
    const match = /\/realms\/([^/]+)/.exec(this.url);
    return match?.[1] ?? "opendesk";
  }

  /** Schedule a token refresh 5 minutes before expiry. */
  private scheduleRefresh(): void {
    this.clearRefreshTimer();

    const kc = this.keycloakInstance;
    if (!kc?.tokenParsed?.exp) return;

    const expiresIn = kc.tokenParsed.exp * 1000 - Date.now();
    // Refresh 5 minutes before expiry, minimum 10 seconds.
    const refreshIn = Math.max(expiresIn - 5 * 60 * 1000, 10_000);

    this.refreshTimer = setTimeout(async () => {
      const success = await this.refreshToken();
      if (!success) {
        // Token refresh failed, force re-login.
        this.login();
      }
    }, refreshIn);
  }

  /** Clear any pending refresh timer. */
  private clearRefreshTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}

/**
 * Map Keycloak realm roles to the operator API role.
 * Highest-privilege role wins (matches server-side mapRole in auth.go).
 */
function mapRole(roles: string[]): Role {
  if (roles.includes("opendesk-platform-admin")) return "platform-admin";
  if (roles.includes("opendesk-tenant-admin")) return "tenant-admin";
  return "tenant-user";
}

// Keycloak type stub for import.meta.env usage.
declare global {
  interface ImportMetaEnv {
    readonly VITE_KEYCLOAK_URL?: string;
    readonly VITE_API_URL?: string;
  }
  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}
